import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import { Toaster, toast } from "react-hot-toast";
import OverlayLoader from "../Components/OverlayLoader";
import CollapsibleSidebar from "../Components/CollapsibleSidebar";
import { GetLeadsByIds } from "../Services/APIService";




interface LeadDetail {
    leadId: number;
    leadName: string;
    mobile: string;
    countryCode:string;
    email: string;
    source: string;
    subSource: string;
    status: string;
    assignedTo: string;
}

const UserActivityDetails: React.FC = () => {
    const navigate = useNavigate();

    const hash = window.location.hash;
    const queryString = hash.split("?")[1] || "";

    const searchParams = new URLSearchParams(queryString);

    const leadIdsParam = searchParams.get("leadIds");

    const leadIds = leadIdsParam
        ? leadIdsParam.split(",").map(Number)
        : [];

    const userName = searchParams.get("userName") || "User";
    const status = searchParams.get("statusName") || "Status";

    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [loading, setLoading] = useState(true);
    const [leadDetails, setLeadDetails] = useState<LeadDetail[]>([]);
    const [searchText, setSearchText] = useState("");

    const toggleSidebar = () => setIsSidebarCollapsed(!isSidebarCollapsed);

    const fetchLeadDetails = async () => {
        try {
            setLoading(true);
            const result = await GetLeadsByIds(leadIds);
            setLeadDetails(result);
           // toast.success("Lead details loaded successfully");
        } catch (error: any) {
            console.error(error);
            toast.error(`Failed to load lead details: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!leadIds.length) {
            toast.error("No lead IDs provided");
            return;
        }

        fetchLeadDetails();
    }, []);

    const filteredLeads = leadDetails.filter((lead) => {
        const text = searchText.toLowerCase().trim();
        if (!text) return true;

        return (
            lead.leadName.toLowerCase().includes(text) ||
            lead.mobile.includes(text) ||
            lead.email.toLowerCase().includes(text)
        );
    });

    const columns = [
        {
            name: "Lead Name",
            selector: (row: LeadDetail) => row.leadName,
            sortable: true,
            cell: (row: LeadDetail) => (
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <div
                        style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "50%",
                            backgroundColor: "rgb(167, 133, 144)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                            fontSize: "14px",
                            fontWeight: "bold",
                        }}
                    >
                        {row.leadName.charAt(0).toUpperCase() || "?"}
                    </div>
                    <a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            navigate("/LeadDashboard", { state: { leadId: row.leadId } });
                        }}
                        style={{ textDecoration: "none", color: "#33475b", fontWeight: 500 }}
                    >
                        {row.leadName}
                    </a>
                </div>
            ),
        },
        //{ name: "Mobile", selector: (row: LeadDetail) => row.mobile, sortable: true },
        {
            name: "Mobile",
            cell: (row: LeadDetail) => (
                <span>
                    {`${row.countryCode || ""} ${row.mobile || ""}`.trim() || "N/A"}
                </span>
            ),
            sortable: true,
            width: "150px",
        },
        { name: "Email", selector: (row: LeadDetail) => row.email, sortable: true },
        { name: "Source", selector: (row: LeadDetail) => row.source, sortable: true },
        { name: "Sub Source", selector: (row: LeadDetail) => row.subSource, sortable: true },
        {
            name: "Status",
            selector: (row: LeadDetail) => row.status,
            sortable: true,
            cell: (row: LeadDetail) => <span className="badge bg-success">{row.status}</span>,
        },
    ];

    const exportToExcel = () => {
        const headers = ["Lead Name", "Mobile", "Email", "Source", "Sub Source", "Status"];
        const rows = filteredLeads.map((l) => [
            l.leadName,
           `${l.countryCode || ""} ${l.mobile || ""}`.trim() || "N/A",
            l.email,
            l.source,
            l.subSource,
            l.status,
        ]);

        let csv = headers.join(",") + "\n";
        rows.forEach((r) => {
            csv += r.map((c) => `"${c}"`).join(",") + "\n";
        });

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${status}-${userName}.csv`;
        link.click();
    };

    const exportToPDF = () => {
        const htmlContent = `
      <html>
        <head>
          <title>Lead Details - ${status}</title>
          <style>
            body { font-family: Arial; padding: 20px; }
            h1 { text-align: center; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #ddd; padding: 8px; }
            th { background-color: #a78590; color: white; }
            tr:nth-child(even) { background-color: #f9f9f9; }
          </style>
        </head>
        <body>
          <h1>Lead Details - ${status}</h1>
          <p>User: ${userName} | Total Records: ${filteredLeads.length}</p>
          <table>
            <thead>
              <tr>
                <th>Lead Name</th>
                <th>Mobile</th>
                <th>Email</th>
                <th>Source</th>
                <th>Sub Source</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${filteredLeads
                .map(
                    (l) => `<tr>
                    <td>${l.leadName}</td>
                    <td>${l.countryCode}${l.mobile}</td>
                    <td>${l.email}</td>
                    <td>${l.source}</td>
                    <td>${l.subSource}</td>
                    <td>${l.status}</td>
                  </tr>`
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;

        const printWindow = window.open("", "_blank");
        if (printWindow) {
            printWindow.document.write(htmlContent);
            printWindow.document.close();
            printWindow.onload = () => printWindow.print();
        } else {
            toast.error("Please allow pop-ups to export PDF");
        }
    };

    return (
        <div className="wrapper">
            <Toaster
                position="top-center"
                reverseOrder={false}
                containerStyle={{ zIndex: 10000 }}
            />

            {!isSidebarCollapsed && <CollapsibleSidebar />}

            <div className="main">
                <Navbar onToggleSidebar={toggleSidebar} />

                <div className="main-content-div">
                    <div className="main-header-div"></div>

                    <main className="content mainContent">
                        <div className="container-fluid p-0">
                            <div className="row">
                                <div className="col-12">
                                    <div className="card">

                                        {/* HEADER */}
                                        <div className="card-header carHeaderWithBtn">
                                            <div className="card-title mb-0">
                                                Lead Details
                                                <div style={{ fontSize: "12px", fontWeight: 400 }}>
                                                    User: {userName} | Status: {status}
                                                </div>
                                            </div>

                                            <div style={{ display: "flex", gap: "10px" }}>
                                                <input
                                                    type="text"
                                                    placeholder="Search by Name, Email or Mobile"
                                                    className="p-2 datatableSearchInput"
                                                    value={searchText}
                                                    onChange={(e) => setSearchText(e.target.value)}
                                                    style={{ width: "260px" }}
                                                />

                                                <button
                                                    className="demo-btn btn btn-outline-success btn-sm"
                                                    onClick={exportToPDF}
                                                    title="Export to PDF"
                                                >
                                                    <i className="bi bi-file-pdf"></i> Export PDF
                                                </button>

                                                <button
                                                    className="demo-btn btn btn-outline-success btn-sm"
                                                    onClick={exportToExcel}
                                                    title="Export to Excel"
                                                >
                                                    <i className="bi bi-file-excel"></i> Export Excel
                                                </button>
                                                <button
                                                    className="demo-btn btn btn-outline-secondary btn-sm"
                                                    onClick={() => window.close()}
                                                    title="Close"
                                                >
                                                    <i className="bi bi-x-lg"></i>
                                                </button>

                                            </div>
                                        </div>

                                        {/* TABLE */}
                                        <div className="card-body p-0">
                                            <DataTable
                                                columns={columns}
                                                data={filteredLeads}
                                                pagination
                                                paginationPerPage={10}
                                                highlightOnHover
                                                striped
                                                responsive
                                                noDataComponent="No leads found"
                                            />
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </div>
                    </main>

                    <Footer />
                </div>
            </div>

            <OverlayLoader
                loading={loading}
                message="Loading Lead Details"
                submessage="Please wait..."
            />

            {/* SAME STYLES AS USER ACTIVITY */}
            <style>{`
      .rdt_TableCol:first-of-type {
        padding-left: 16px !important;
      }

      .datatableSearchInput {
        border-radius: 4px;
        border: 1px solid #ced4da;
        font-size: 14px;
      }

      .demo-btn {
        display: flex;
        align-items: center;
        gap: 6px;
      }

      .carHeaderWithBtn {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .css-1nmdiq5-menu {
        position: relative;
        z-index: 10000;
      }
    `}</style>
        </div>
    );

};

export default UserActivityDetails;
